/**
 * posthog-js's own scrubbing of network bodies in session replays, which it skips once
 * `session_recording.maskCapturedNetworkRequestFn` is set. This project records request and response
 * bodies, so the replay hook in `session-replay-masking.ts` has to apply it itself. Unlike posthog,
 * it matches the deny list without regard to case, so keys such as `accessToken`, `apiKey` and
 * `Password` count too.
 *
 * Adapted from posthog-js 1.425.1 (`scrubPayload` in lib/src/extensions/replay/external/config.js)
 * and @posthog/browser-common 0.7.1 (`shouldCaptureValue` and its card and SSN detectors in
 * dist/utils/autocapture-utils.js). Copyright (c) PostHog Inc., MIT License.
 */

const PAYLOAD_CONTENT_DENY_LIST = [
  'password',
  'secret',
  'passwd',
  'api_key',
  'apikey',
  'auth',
  'credentials',
  'mysql_pwd',
  'privatekey',
  'private_key',
  'token',
];

const ANCHORED_CARD_REGEX =
  /^(?:4\d{12}(?:\d{3})?|5[1-5]\d{14}|6(?:011|5\d{2})\d{12}|3[47]\d{13}|3(?:0[0-5]|[68]\d)\d{11}|(?:2131|1800|35\d{3})\d{11})$/;
const CARD_CANDIDATE_REGEX = /(?:^|\W)(\d[\d -]*\d)(?=$|\W)/g;
const CARD_LENGTHS = [16, 15, 14, 13];
const SSN_CANDIDATE_REGEX = /(^|\D)(?!000|666)\d{3}-?(?!00)\d{2}-?(?!0000)\d{4}(?=$|(\D))/g;
const IDENTIFIER_CHARACTER_REGEX = /\w/;

/** Returns the body unchanged, or a redaction notice when it may hold a credential, card or SSN. */
export function scrubNetworkPayload(
  payload: string | null | undefined,
  label: 'Request' | 'Response',
): string | null | undefined {
  if (payload === null || payload === undefined) {
    return payload;
  }

  if (containsCardNumber(payload.trim()) || containsSSN(payload.trim())) {
    return `[SessionRecording] ${label} body redacted`;
  }
  const lowercased = payload.toLowerCase();
  const deniedText = PAYLOAD_CONTENT_DENY_LIST.find((text) => lowercased.includes(text));
  return deniedText === undefined
    ? payload
    : `[SessionRecording] ${label} body redacted as might contain: ${deniedText}`;
}

function passesLuhnCheck(value: string): boolean {
  let sum = 0;
  let shouldDouble = false;
  for (let index = value.length - 1; index >= 0; index--) {
    let digit = value.charCodeAt(index) - 48;
    if (shouldDouble) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }
    sum += digit;
    shouldDouble = !shouldDouble;
  }
  return sum % 10 === 0;
}

function containsCardNumber(value: string): boolean {
  for (const match of value.matchAll(CARD_CANDIDATE_REGEX)) {
    const candidate = match[1]?.replace(/[- ]/g, '');
    if (!candidate) {
      continue;
    }
    for (let start = 0; start < candidate.length; start++) {
      for (const length of CARD_LENGTHS) {
        const end = start + length;
        if (end <= candidate.length) {
          const possibleCardNumber = candidate.slice(start, end);
          if (ANCHORED_CARD_REGEX.test(possibleCardNumber) && passesLuhnCheck(possibleCardNumber)) {
            return true;
          }
        }
      }
    }
  }
  return false;
}

function containsSSN(value: string): boolean {
  for (const match of value.matchAll(SSN_CANDIDATE_REGEX)) {
    const characterBefore = match[1];
    const characterAfter = match[2];
    if (
      characterBefore &&
      characterAfter &&
      IDENTIFIER_CHARACTER_REGEX.test(characterBefore) &&
      IDENTIFIER_CHARACTER_REGEX.test(characterAfter)
    ) {
      continue;
    }
    return true;
  }
  return false;
}
